import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactSortable } from "react-sortablejs";
import PropTypes from "prop-types";
import { isMobile } from "react-device-detect";

import LinkItemWithCategoryModal from "features/menus/components/LinkItemWithCategoryModal";
import LinkUnlinkItemWithStoresModal from "./LinkUnlinkItemWithStoresModal";
import { useAccordion } from "features/menu/components/AccordionN";
import { errorHandler } from "api/api/errorHandler";
import AppModal from "components/ui/AppModal";
import AppButton from "components/ui/AppButton";
import AppIcon from "components/ui/AppIcon";
import ItemRow from "./ItemRow";
import { UTILS } from "utils";

import { useToggLeStoreInMenuCategoryITemMutation } from "redux/menu/service";
import { logErrorOnSentry } from "utils/Utils";
import { useDispatch } from "react-redux";
import { LoaderAction } from "redux/actions";
import { API } from "api";

const CategoryItem = ({
  setLoading,
  cat,
  connectedStores,
  menuId,
  brandItems,
  loading,
  items,
  setItems,
  getCategoryInfo,
  addItemTrigger,
}) => {
  const ref = useRef();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selected } = useAccordion();
  const [open, setOpen] = useState(false);
  const [dialogContentId, setDialogContentId] = useState(-1);
  const [itemToPassStoresDialog, setItemToPassStoreDialog] = useState();

  const DIALOG_VIEW = {
    ADD_ITEM: 0,
    STORES: 1,
  };

  const style = {
    panel: `overflow-hidden md:overflow-x-hidden transition-height ease text-gray-600 text-16px`,
  };

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    item: null,
    indexToDelete: -1,
  });
  const [isStoreItemModalOpen, setisStoreItemModalOpen] = useState(false);
  const inlineStyle =
    selected === cat?._id
      ? { height: ref.current?.scrollHeight }
      : { height: 0 };

  const addItemDialogOpen =
    open && dialogContentId === DIALOG_VIEW.ADD_ITEM;

  const closeAddItemDialog = () => {
    setOpen(false);
    setDialogContentId(-1);
  };
  const handleStores = (item, index) => {
    setItemToPassStoreDialog(item);
    setisStoreItemModalOpen(true);
  };

  const [toggLeStoreInMenuCategoryITem] =
    useToggLeStoreInMenuCategoryITemMutation();

  const removeItemFromStore = async (sId, itemId, val, selectedCatory) => {
    try {
      const userDetail = UTILS.getCurrentUser();
      setLoading(true);
      dispatch(LoaderAction.setLoading(true));

      const result = await toggLeStoreInMenuCategoryITem({
        brandId: userDetail.brandId,
        menuId,
        categoryId: selectedCatory._id,
        itemId,
        storeId: sId,
      });

      if (result?.data?.success) {
        let arr = [...itemToPassStoresDialog?.unConnectedStoreIds];
        if (!val) {
          //add
          arr.push(sId);
          setItemToPassStoreDialog((prevState) => ({
            ...prevState,
            unConnectedStoreIds: arr,
          }));
        } else {
          //remove
          arr.splice(
            arr.findIndex((id) => id === sId),
            1
          );
          setItemToPassStoreDialog((prevState) => ({
            ...prevState,
            unConnectedStoreIds: arr,
          }));
        }
        await getCategoryInfo(selectedCatory?._id);
      } else {
        UTILS.errorNotification(errorHandler(result?.error?.data));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.message));
    } finally {
      setLoading(false);
      dispatch(LoaderAction.setLoading(false));
    }
  };

  const reOrderCategory = async (order) => {
    setLoading(true);
    dispatch(LoaderAction.setLoading(true));
    try {
      let item = items[order.oldIndex];
      const userDetail = UTILS.getCurrentUser();
      const result = await API.sortItemInCategory({
        brandId: userDetail.brandId,
        menuId,
        categoryId: cat._id,
        itemId: item.itemId._id,
        index: order.newIndex,
      });
      if (result?.success === 1) {
        let arr = [...items];
        arr.splice(order.oldIndex, 1);
        arr.splice(order.newIndex, 0, item);
        setItems(arr);
      } else {
        UTILS.errorNotification(errorHandler(result?.message));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally {
      setLoading(false);
      dispatch(LoaderAction.setLoading(false));
    }
  };

  const unLinkMenuItemFromCategory = async () => {
    try {
      const brandId = UTILS.getCurrentUser()?.brandId;
      if (!deleteModal?.item?._id || !cat?._id || !menuId) {
        setDeleteModal({ isOpen: false, item: null, indexToDelete: -1 });
        logErrorOnSentry(
          `unLinkMenuItemFromCategory ${brandId}`,
          "error",
          deleteModal
        );
        UTILS.errorNotification(
          t("We're sorry, but something went wrong. Please try again")
        );
        return;
      }
      setLoading(true);
      const result = await API.unLinkItemWithCategory({
        brandId,
        categoryId: cat?._id,
        itemId: deleteModal?.item?._id,
        menuId,
      });

      if (result?.success === 1) {
        let arr = [...items];
        arr.splice(deleteModal.indexToDelete, 1);
        setItems(arr);
        setDeleteModal({ isOpen: false, item: null, indexToDelete: -1 });
        UTILS.successNotification(result?.message || "Item Unlinked");
      } else UTILS.errorNotification(errorHandler(result?.message));
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () =>
    setDeleteModal({ isOpen: false, item: null });

  const onClickDeleteButton = (item, index) => {
    if (item) {
      setDeleteModal({
        isOpen: true,
        item: item,
        indexToDelete: index,
      });
    }
  };

  useEffect(() => {
    if (
      addItemTrigger?.categoryId === cat?._id &&
      addItemTrigger?.nonce
    ) {
      setDialogContentId(DIALOG_VIEW.ADD_ITEM);
      setOpen(true);
      getCategoryInfo(cat?._id, { silent: true });
    }
  }, [addItemTrigger, cat?._id, getCategoryInfo]);

  const deleteFooter = (
    <div className="mx-auto flex w-full max-w-[760px] items-center justify-center gap-4 pb-2">
      <AppButton
        variant="secondary"
        size="lg"
        className="w-1/2 border border-gray-400 bg-white text-4xl text-gray-500"
        onClick={handleDeleteCancel}
        disabled={loading}
      >
        {t("No, Keep It")}
      </AppButton>
      <AppButton
        variant="primary"
        size="lg"
        className="w-1/2 text-4xl"
        onClick={unLinkMenuItemFromCategory}
        loading={loading}
        disabled={loading}
      >
        {t("Yes, Delete")}
      </AppButton>
    </div>
  );

  return (
    <div ref={ref} id={cat?._id} className={style.panel} style={inlineStyle}>
      <LinkItemWithCategoryModal
        onClose={closeAddItemDialog}
        open={addItemDialogOpen}
        cat={cat}
        menuId={menuId}
        brandItems={brandItems}
        getCategoryInfo={getCategoryInfo}
        items={items}
      />
      <AppModal
        open={deleteModal.isOpen}
        width={550}
        onClose={handleDeleteCancel}
        closable={false}
        footer={deleteFooter}
      >
        <div className="flex flex-col items-center px-8 text-center">
          <div className="mb-6 mt-6 h-44 w-44">
            <AppIcon name="trashRed" size={170} className="h-full w-full" />
          </div>
          <h1 className="text-24px font-semibold text-gray-900">
            {t("Delete Item")}
          </h1>
          <p className="mb-0 text-3xl text-gray-500">
            {deleteModal.item?.baseLanguage?.title
              ? t("Are you sure you want to delete {{name}}?", {
                  name: deleteModal.item.baseLanguage.title,
                })
              : t("Are you sure you want to delete this Item")}
          </p>
        </div>
      </AppModal>
      <AppModal
        open={isStoreItemModalOpen}
        onClose={() => {
          setisStoreItemModalOpen(false);
        }}
        title={
          <div className="truncate-line-3 uppercase me-6 md:me-8">
            {itemToPassStoresDialog?.itemId?.baseLanguage?.title || ""}
          </div>
        }
        width={500}
        bodyStyle={{ overflowY: "auto", maxHeight: "calc(100vh - 500px)" }}
        
      >
        <LinkUnlinkItemWithStoresModal
          {...{
            connectedStores,
            removeItemFromStore,
            loading,
          }}
          cat={cat}
          key={cat?._id}
          onClose={() => setOpen(false)}
          item={itemToPassStoresDialog}
        />
      </AppModal>
      {isMobile ? (
        <>
          {items?.map((item, index) => {
            return (
              <ItemRow
                key={item?.itemId?._id}
                item={item}
                deleteItem={unLinkMenuItemFromCategory}
                index={index}
                handleStores={handleStores}
                getCategoryInfo={getCategoryInfo}
                cat={cat}
              />
            );
          })}
        </>
      ) : (
        <ReactSortable
          list={items}
          setList={setItems}
          animation={300}
          onUpdate={(order) => {
            reOrderCategory(order);
          }}
          className="mb-3"
        >
          {items &&
            items.map((item, index) => {
              return (
                <ItemRow
                  key={item?.itemId?._id}
                  item={item}
                  deleteItem={onClickDeleteButton}
                  index={index}
                  handleStores={handleStores}
                  getCategoryInfo={getCategoryInfo}
                  cat={cat}
                  
                />
              );
            })}
        </ReactSortable>
      )}

    </div>
  );
};
export default CategoryItem;

CategoryItem.propTypes = {
  cat: PropTypes.object,
  addItemTrigger: PropTypes.shape({
    categoryId: PropTypes.string,
    nonce: PropTypes.number,
  }),
  handleCount: PropTypes.func,
  pushFrom: PropTypes.string,
};
